import { countOf } from '../../domain/labels.js';
import { listOf } from '../../domain/pluralise.js';
import type { MatrixColumn, Rollup } from '../../components/Matrix/types.js';

export type InboxSource = { environment: string; count: number };

/**
 * Where games master does not hold yet are waiting, environment by environment. They are never
 * rows of this grid — rows come from master (`service/matrix.ts`) — so without this a game
 * added straight to an environment and pulled seems to vanish: it went to that environment's
 * inbox. Across the catalogue the count is the column's own `game` env-only count; with a studio
 * chosen it is that studio's roll-up instead, because the inbox drawer opened from this page is
 * narrowed to the same studio (`MatrixOverlays`'s `inboxStudio`). A failed pull's counts are the
 * last good pull's, so that column is left out.
 */
export const gamesInboxSources = (
    columns: readonly Pick<MatrixColumn, 'name' | 'pullFailed' | 'countsByType'>[],
    studioRollups: Record<string, Rollup> | null,
): InboxSource[] =>
    columns.flatMap((column) => {
        if (column.pullFailed) return [];
        const rollup = studioRollups?.[column.name];
        const count = studioRollups
            ? rollup?.kind === 'issues'
                ? rollup.env_only
                : 0
            : (column.countsByType.game?.env_only ?? 0);
        return count > 0 ? [{ environment: column.name, count }] : [];
    });

export const gamesInboxSentence = (sources: readonly InboxSource[]): string => {
    const total = sources.reduce((sum, s) => sum + s.count, 0);
    const where = listOf(sources.map((s) => s.environment));
    return `${countOf('game', total)} in ${where} ${total === 1 ? 'is' : 'are'} not in master yet.`;
};
